import React, { useState, useRef, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { Input } from './input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';

export type NumberInputUnitOption = { value: string; label: string };

interface NumberInputProps {
  id?: string;
  value: string | number;
  onChange: (value: string) => void;
  onFocus?: () => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  className?: string;
  min?: number;
  step?: number;
  disabled?: boolean;
  max?: number;
  /** When true, unit dropdown is shown beside the input. Default false (hidden). */
  showUnitSelect?: boolean;
  unitValue?: string;
  unitOptions?: NumberInputUnitOption[];
  onUnitChange?: (value: string) => void;
  /** Classes for the horizontal row wrapper when showUnitSelect is true (e.g. mt-2). */
  unitSelectWrapperClassName?: string;
  /** Max digits after decimal point while typing (e.g. 3 for mm, 4 for inches). */
  maxDecimalPlaces?: number;
  /** When false, show "0" instead of empty placeholder. Default true (0 shows as empty). */
  emptyWhenZero?: boolean;
  /** When true, mouse wheel over focused input adjusts value by step (default false = wheel blocked). */
  wheelAdjust?: boolean;
}

export function parseNonNegativeNumber(raw: string, fallback = 0): number {
  const n = parseFloat(raw);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, n);
}

export function parseNonNegativeInteger(raw: string | number, fallback = 0): number {
  const n = typeof raw === 'number' ? raw : parseFloat(raw);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.floor(n));
}

export const NumberInput: React.FC<NumberInputProps> = ({
  id,
  value,
  onChange,
  onFocus,
  onBlur,
  placeholder,
  className,
  min,
  step = 0.01,
  disabled,
  max,
  showUnitSelect = false,
  unitValue,
  unitOptions,
  onUnitChange,
  unitSelectWrapperClassName,
  maxDecimalPlaces,
  emptyWhenZero = true,
  wheelAdjust = false,
}) => {
  const [displayValue, setDisplayValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const formatValueForDisplay = (val: string | number): string => {
    if (val === '' || val === 0 || val === '0') return '';
    const n = typeof val === 'number' ? val : Number(val);
    if (!Number.isFinite(n)) return String(val);
    if (maxDecimalPlaces == null) return String(val);
    return n.toFixed(maxDecimalPlaces).replace(/\.?0+$/, '');
  };

  useEffect(() => {
    if (value === '' || value === null || value === undefined) {
      setDisplayValue('');
      return;
    }
    if ((value === 0 || value === '0') && emptyWhenZero) {
      setDisplayValue('');
      return;
    }
    setDisplayValue(formatValueForDisplay(value));
  }, [value, maxDecimalPlaces, emptyWhenZero]);

  const normalizeNumber = (inputValue: string): string => {
    // If input is empty, return empty string
    if (inputValue === '' || inputValue === null || inputValue === undefined) {
      return '';
    }

    // Handle decimal numbers - preserve leading zero for decimals like 0.5
    if (inputValue.includes('.')) {
      const parts = inputValue.split('.');
      const wholePart = parts[0];
      const decimalPart = parts[1];

      // Remove leading zeros from whole part but keep single zero
      const normalizedWhole = wholePart.replace(/^0+/, '') || '0';

      return `${normalizedWhole}.${decimalPart}`;
    }

    // For whole numbers, remove leading zeros but keep single zero
    const trimmed = inputValue.replace(/^0+/, '');
    return trimmed === '' ? '0' : trimmed;
  };

  const handleFocus = () => {
    // Only normalize if there's actually a value to normalize
    if (displayValue && displayValue.trim() !== '') {
      const normalized = normalizeNumber(displayValue);
      setDisplayValue(normalized);
    }
    onFocus?.();
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Only normalize if there's actually a value to normalize
    if (displayValue && displayValue.trim() !== '') {
      const normalized = normalizeNumber(displayValue);
      setDisplayValue(normalized);
    }
    onBlur?.(e);
  };

  const clampDecimalPlaces = (inputValue: string): string => {
    if (maxDecimalPlaces == null || !inputValue.includes('.')) return inputValue;
    const [whole, decimal = ''] = inputValue.split('.');
    if (decimal.length <= maxDecimalPlaces) return inputValue;
    return `${whole}.${decimal.slice(0, maxDecimalPlaces)}`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;

    if (min != null && min >= 0 && (newValue.startsWith('-') || newValue.includes('e'))) {
      return;
    }

    // If step is 1, don't allow decimal points
    if (step === 1 && newValue.includes('.')) {
      return; // Keep the old value, don't update
    }

    newValue = clampDecimalPlaces(newValue);

    setDisplayValue(newValue);
    onChange(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (min != null && min >= 0 && (e.key === '-' || e.key === 'e' || e.key === 'E')) {
      e.preventDefault();
    }
    // If step is 1, prevent decimal point key
    if (step === 1 && e.key === '.') {
      e.preventDefault();
    }
  };

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Wheel: block accidental scroll, or adjust value when wheelAdjust is enabled
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (document.activeElement !== el) return;
      e.preventDefault();
      if (!wheelAdjust) return;

      const raw = el.value.trim();
      const current = raw === '' ? 0 : Number(raw);
      if (!Number.isFinite(current)) return;

      const delta = e.deltaY < 0 ? step : -step;
      let next = current + delta;
      if (min != null) next = Math.max(min, next);
      if (max != null) next = Math.min(max, next);
      if (step === 1) {
        next = Math.round(next);
      } else if (maxDecimalPlaces != null) {
        const factor = 10 ** maxDecimalPlaces;
        next = Math.round(next * factor) / factor;
      }

      const nextStr =
        next === 0 && emptyWhenZero
          ? ''
          : maxDecimalPlaces != null && step !== 1
            ? String(next)
            : String(parseNonNegativeNumber(String(next), next));
      setDisplayValue(nextStr);
      onChangeRef.current(nextStr);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [wheelAdjust, step, min, max, emptyWhenZero]);

  const unitRowActive =
    showUnitSelect &&
    !!unitOptions?.length;
  const selectedUnitValue = unitValue || unitOptions?.[0]?.value;
  const hasError = typeof className === 'string' && className.includes('border-red');

  const inputEl = (
    <Input
      ref={inputRef}
      id={id}
      type="number"
      min={min}
      step={step}
      max={max}
      value={displayValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      className={cn(
        unitRowActive &&
          'h-10 min-h-10 min-w-0 w-full rounded-none border-0 border-r border-input bg-transparent py-0 leading-10 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 [-moz-appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
        className
      )}
      disabled={disabled}
    />
  );

  if (!unitRowActive) {
    return inputEl;
  }

  return (
    <div
      className={cn(
        'grid h-10 w-full min-h-10 grid-cols-[minmax(0,70%)_minmax(90px,30%)] items-stretch gap-0 overflow-visible rounded-lg border bg-white ring-offset-background transition-colors',
        hasError ? 'border-readable-red' : 'border-[#E2E5EA] hover:border-heritage-grey',
        'focus-within:border-hero-red focus-within:ring-2 focus-within:ring-hero-red/30',
        unitSelectWrapperClassName
      )}
    >
      {inputEl}
      <Select
        value={selectedUnitValue}
        onValueChange={(value) => onUnitChange?.(value)}
        disabled={disabled}
      >
        <SelectTrigger className="h-10 min-h-10 w-full rounded-none border-0 border-l border-[#E2E5EA] bg-transparent px-2 text-xs shadow-none focus:ring-0 focus:ring-offset-0 data-[state=open]:ring-0">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {unitOptions.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
